/**
 * UserFilters Component
 * Filtres de recherche pour la liste des utilisateurs
 */

import { Search, X } from 'lucide-react';
import type { UserFiltersState } from '../../application/dto/dtos';

export type { UserFiltersState };

interface UserFiltersProps {
  filters: UserFiltersState;
  onFilterChange: (filters: UserFiltersState) => void;
  onReset: () => void;
}

export function UserFilters({ filters, onFilterChange, onReset }: UserFiltersProps) {
  const handleChange = (field: keyof UserFiltersState, value: string) => {
    onFilterChange({
      ...filters,
      [field]: value,
    });
  };

  const hasActiveFilters =
    filters.searchTerm ||
    filters.role ||
    filters.isActive ||
    filters.deuxFacteursActive ||
    filters.lockedAccount;

  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-content">
          Filtres
        </h3>
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="text-sm text-primary-text hover:underline flex items-center gap-1"
          >
            <X className="w-4 h-4" />
            Réinitialiser
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Recherche textuelle */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-content-muted mb-1">
            Rechercher
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle" />
            <input
              type="text"
              value={filters.searchTerm}
              onChange={(e) => handleChange('searchTerm', e.target.value)}
              placeholder="Nom, email..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-md
                       bg-surface text-content
                       placeholder-content-subtle
                       focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {/* Filtre rôle */}
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">
            Rôle
          </label>
          <select
            value={filters.role}
            onChange={(e) => handleChange('role', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md
                     bg-surface text-content
                     focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Tous</option>
            <option value="SUPER_ADMIN">Super Admin</option>
            <option value="ADMIN_CARITAS">Admin Caritas</option>
            <option value="ADMIN_NATIONAL">Admin National</option>
            <option value="ADMIN_DIOCESAIN">Admin Diocésain</option>
            <option value="GESTIONNAIRE">Gestionnaire</option>
            <option value="ANALYSTE">Analyste</option>
            <option value="OBSERVATEUR">Observateur</option>
          </select>
        </div>

        {/* Filtre statut */}
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">
            Statut
          </label>
          <select
            value={filters.isActive}
            onChange={(e) => handleChange('isActive', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md
                     bg-surface text-content
                     focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Tous</option>
            <option value="true">Actifs</option>
            <option value="false">Inactifs</option>
          </select>
        </div>

        {/* Filtre 2FA */}
        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">
            2FA
          </label>
          <select
            value={filters.deuxFacteursActive}
            onChange={(e) => handleChange('deuxFacteursActive', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md
                     bg-surface text-content
                     focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Tous</option>
            <option value="true">Avec 2FA</option>
            <option value="false">Sans 2FA</option>
          </select>
        </div>
      </div>
    </div>
  );
}
